// TODO: vitest component test (pages are covered by Playwright e2e)
import { useState, useCallback } from 'react'
import { useParams, useNavigate, Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Receipt,
  User,
  Home,
  Calendar,
  Trash2,
  CreditCard,
  CheckCircle,
  AlertCircle,
  Edit,
  Image,
  Download,
  FileText,
  FileBadge,
  PenLine,
  Eye,
} from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal, Input, ConfirmDialog, Textarea } from '@/components/ui'
import { Alert } from '@/components/ui/Alert'
import { useInvoice, useDeleteInvoice, useDeletePayment, useSignInvoice, invoiceKeys } from '@/hooks/useInvoices'
import { useAccountSettings } from '@/hooks/useAccountSettings'
import { useDateFormatter, useDateTimeFormatter, useLocalizationConfig } from '@/hooks/useDateFormatter'
import { formatMonthYear } from '@/lib/date'
import { useDocumentTemplates } from '@/hooks/useDocumentTemplates'
import { useAuth } from '@/hooks/useAuth'
import { formatBaht } from '@/lib/money'
// WEB-H7: PDF generators are dynamic-imported at the call sites — a static
// import here would drag pdfme (~hundreds of KB) into this page's chunk.
import type { Template } from '@pdfme/common'
import { api } from '@/lib/api'
import { useFeatureFlags } from '@/lib/featureFlags'
import { useNotificationStore } from '@/stores/useNotificationStore'
import { SignInvoiceModal } from '@/components/invoices/SignInvoiceModal'
import { PaymentFields } from '@/components/payments/PaymentFields'
import { ExploreCoachBanner } from '@/components/onboarding/ExploreCoachBanner'
import { InvoiceStatusBadge } from '@/components/invoices/InvoiceStatusBadge'

interface TaxInvoiceFormData {
  seller_name: string
  seller_tax_id: string
  seller_address: string
  seller_branch: string
  buyer_name: string
  buyer_tax_id: string
  buyer_address: string
}

// PaymentFormData has been moved to PaymentFields component (task-019)

export function InvoiceDetailPage() {
  const { t, i18n } = useTranslation('invoices')
  const { t: tc } = useTranslation('common')
  const { id } = useParams<{ id: string }>()
  const invoiceId = Number(id)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const featureFlags = useFeatureFlags()

  const { data: invoice, isLoading, isFetching } = useInvoice(invoiceId)
  const { data: accountSettings } = useAccountSettings()
  const formatDate = useDateFormatter()
  const formatDateTime = useDateTimeFormatter()
  const { calendar } = useLocalizationConfig()
  const { data: invoiceTemplates } = useDocumentTemplates(1, 'invoice')
  const { data: receiptTemplates } = useDocumentTemplates(1, 'receipt')
  const { data: taxInvoiceTemplates } = useDocumentTemplates(1, 'tax_invoice')
  const { user } = useAuth()
  const deleteInvoice = useDeleteInvoice()
  const deletePayment = useDeletePayment()
  const signInvoice = useSignInvoice()
  const addToast = useNotificationStore((s) => s.addToast)

  const isAdminOrOwner = user?.role === 'admin' || user?.role === 'owner'
  const [showTaxInvoiceModal, setShowTaxInvoiceModal] = useState(false)
  const [showSignModal, setShowSignModal] = useState(false)

  const {
    register: registerTax,
    handleSubmit: handleSubmitTax,
    reset: resetTax,
    formState: { errors: taxErrors },
  } = useForm<TaxInvoiceFormData>()

  const generateTaxInvoice = useMutation({
    mutationFn: (data: TaxInvoiceFormData) =>
      api.post<{ data: import('@/types').Invoice }>(`/api/invoices/${invoiceId}/generate_tax_invoice`, {
        seller: {
          name: data.seller_name,
          tax_id: data.seller_tax_id,
          address: data.seller_address,
          branch: data.seller_branch,
        },
        buyer: {
          name: data.buyer_name,
          tax_id: data.buyer_tax_id,
          address: data.buyer_address,
        },
      }),
    onSuccess: async (response) => {
      const updatedInvoice = response.data.data
      const tmpl = taxInvoiceTemplates?.data?.[0]?.template as import('@pdfme/common').Template | undefined
      if (tmpl) {
        try {
          const { generateTaxInvoicePdf } = await import('@/lib/taxInvoicePdfGenerator')
          const pdfData = await generateTaxInvoicePdf(updatedInvoice, tmpl, accountSettings)
          const pdfBlob = new Blob([pdfData.buffer as ArrayBuffer], { type: 'application/pdf' })
          const filename = `tax-invoices/${updatedInvoice.tax_invoice_number ?? updatedInvoice.number}.pdf`
          const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
            content_type: 'application/pdf',
            filename,
          })
          await fetch(presigned.url, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/pdf' },
            body: pdfBlob,
          })
          await api.post(`/api/invoices/${invoiceId}/tax_invoice_pdf`, {
            tax_invoice_pdf_url: presigned.key,
          })
        } catch {
          // PDF upload failed silently — invoice data is still saved
        }
      }
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) })
      setShowTaxInvoiceModal(false)
    },
  })

  const openTaxInvoiceModal = () => {
    const settings = accountSettings?.settings
    resetTax({
      seller_name: invoice?.property?.name || accountSettings?.name || '',
      seller_tax_id: invoice?.property?.tax_id || settings?.tax_id || '',
      seller_address: invoice?.property?.address || settings?.tax_address || '',
      seller_branch: invoice?.property?.branch_number || settings?.branch_number || '',
      buyer_name: invoice?.customer?.name ?? '',
      buyer_tax_id: invoice?.customer?.tax_id ?? '',
      buyer_address: invoice?.customer?.address ?? '',
    })
    setShowTaxInvoiceModal(true)
  }

  const onSubmitTaxInvoice = (data: TaxInvoiceFormData) => {
    generateTaxInvoice.mutate(data)
  }

  const handleSignInvoice = async (signedPdfKey: string) => {
    await signInvoice.mutateAsync({ id: invoiceId, signed_pdf_key: signedPdfKey })
    addToast({ type: 'success', message: t('messages.sign_success') })
    setShowSignModal(false)
  }

  const [isDownloading, setIsDownloading] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [downloadingReceiptId, setDownloadingReceiptId] = useState<number | null>(null)
  const [showPaymentModal, setShowPaymentModal] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [confirmDeletePaymentId, setConfirmDeletePaymentId] = useState<number | null>(null)

  const handleDelete = useCallback(async () => {
    await deleteInvoice.mutateAsync(invoiceId)
    navigate('/invoices')
  }, [deleteInvoice, invoiceId, navigate])

  const handleDownloadPdf = async () => {
    if (!invoice) return
    // If signed and not stale, download the stored signed PDF
    if (invoice.signed_pdf_key && !isSignedStale) {
      window.open(`${import.meta.env.VITE_API_URL || ''}/api/images/${invoice.signed_pdf_key}`, '_blank', 'noopener,noreferrer')
      return
    }
    // Otherwise generate on-the-fly
    const tmpl = invoiceTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) return
    setIsDownloading(true)
    try {
      const { downloadInvoicePdf } = await import('@/lib/invoicePdfGenerator')
      await downloadInvoicePdf(invoice, tmpl, accountSettings)
    } finally {
      setIsDownloading(false)
    }
  }

  const handleDownloadTaxInvoicePdf = () => {
    if (!invoice?.tax_invoice_pdf_url) return
    window.open(
      `${import.meta.env.VITE_API_URL || ''}/api/images/${invoice.tax_invoice_pdf_url}`,
      '_blank'
    )
  }

  const vatEnabled = accountSettings?.settings?.vat_enabled ?? false
  const vatRate = vatEnabled ? (accountSettings?.settings?.vat_rate ?? 7) : 0
  const currentVatRate = vatRate
  const isTaxInvoiceStale =
    invoice !== undefined &&
    invoice.tax_invoice_number != null &&
    invoice.tax_vat_rate !== undefined &&
    invoice.tax_vat_rate !== null &&
    invoice.tax_vat_rate !== currentVatRate

  const isSignedStale =
    invoice !== undefined &&
    invoice.signed_at != null &&
    invoice.signed_total != null &&
    Number(invoice.signed_total) !== Number(invoice.total)

  // VAT breakdown for items table.
  //
  // PricingEngine appends `_vat` sibling line items directly into
  // invoice.items and into invoice.total. So invoice.total is always the
  // inclusive persisted value (VAT already baked in when _vat items exist).
  //
  // Display rule to satisfy: Subtotal + VAT == Total (no double-counting).
  //
  // Case A — engine _vat items present in this invoice:
  //   Subtotal = sum of non-_vat items
  //   VAT      = sum of _vat items            (exact, no fabricated math)
  //   Total    = invoice.total (= Subtotal + VAT)
  //
  // Case B — no _vat items (manual invoice, old record, vatEnabled=false):
  //   Skip the VAT/Subtotal tfoot rows entirely so we never show a
  //   fabricated VAT amount that contradicts invoice.total.
  const vatItems = invoice?.items?.filter((item) => item.item_type.endsWith('_vat')) ?? []
  const hasEngineVatItems = vatItems.length > 0
  // Non-_vat items only (so Subtotal + VAT = invoice.total exactly)
  const nonVatItemsSubtotal =
    invoice?.items?.reduce(
      (sum, item) => sum + (item.item_type.endsWith('_vat') ? 0 : Number(item.amount)),
      0,
    ) ?? 0
  const engineVatAmount = vatItems.reduce((sum, item) => sum + Number(item.amount), 0)
  // Show VAT tfoot rows only when _vat items are actually present
  const showVatBreakdown = hasEngineVatItems

  // Payment difference warning
  const totalPaid = invoice?.amount_paid ?? 0
  const paymentDiff = invoice?.paid ? totalPaid - Number(invoice.total) : 0

  const handleRegeneratePdf = async () => {
    const tmpl = taxInvoiceTemplates?.data?.[0]?.template as import('@pdfme/common').Template | undefined
    if (!tmpl || !invoice) return
    setIsRegenerating(true)
    try {
      const { generateTaxInvoicePdf } = await import('@/lib/taxInvoicePdfGenerator')
      const pdfData = await generateTaxInvoicePdf(invoice, tmpl, accountSettings)
      const pdfBlob = new Blob([pdfData.buffer as ArrayBuffer], { type: 'application/pdf' })
      const filename = `tax-invoices/${invoice.tax_invoice_number ?? invoice.number}.pdf`
      const { data: presigned } = await api.post<{ url: string; key: string }>('/api/presigned_urls', {
        content_type: 'application/pdf',
        filename,
      })
      await fetch(presigned.url, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/pdf' },
        body: pdfBlob,
      })
      await api.post(`/api/invoices/${invoiceId}/tax_invoice_pdf`, {
        tax_invoice_pdf_url: presigned.key,
        tax_vat_rate: currentVatRate,
      })
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(invoiceId) })
    } finally {
      setIsRegenerating(false)
    }
  }

  const handleDownloadReceipt = async (paymentId: number) => {
    if (!invoice) return
    const payment = invoice.payments?.find((p) => p.id === paymentId)
    if (!payment) return
    const tmpl = receiptTemplates?.data?.[0]?.template as Template | undefined
    if (!tmpl) return
    setDownloadingReceiptId(paymentId)
    try {
      const { downloadReceiptPdf } = await import('@/lib/receiptPdfGenerator')
      await downloadReceiptPdf(payment, invoice, tmpl, accountSettings)
    } finally {
      setDownloadingReceiptId(null)
    }
  }

  // PAY-2 (T149-085): "Record Payment" opens the dialog directly. There is no
  // longer a bounce to Settings when no method is enabled. PaymentFields
  // falls back to cash, and shows its own note pointing at Settings.
  const openPaymentModal = () => {
    setShowPaymentModal(true)
  }

  const handleOpenTaxInvoice = () => {
    if (!accountSettings?.settings?.tax_id) {
      addToast({ type: 'info', message: t('tax_invoice.no_tax_id_hint') })
      navigate('/settings?tab=account&focus=tax_id')
      return
    }
    openTaxInvoiceModal()
  }

  const handleDeletePayment = useCallback(async () => {
    if (confirmDeletePaymentId === null) return
    await deletePayment.mutateAsync({ id: confirmDeletePaymentId, invoiceId })
    setConfirmDeletePaymentId(null)
  }, [confirmDeletePaymentId, deletePayment, invoiceId])

  if (isLoading) {
    return (
        <div className="space-y-6">
          <Skeleton className="h-8 w-64" />
          <Card>
            <Skeleton className="h-48" />
          </Card>
        </div>
    )
  }

  if (!invoice) {
    return (
        <Card>
          <EmptyState
            title={t('empty.not_found')}
            action={<Button onClick={() => navigate('/invoices')}>{t('actions.back_to_invoices')}</Button>}
          />
        </Card>
    )
  }

  const canEdit = (!invoice.paid && (invoice.amount_paid ?? 0) === 0) || user?.role === 'owner'

  // MON-9/MON-10: the period the invoice bills for, distinct from billed_at
  // (the real issue date). billing_period is "YYYY-MM"; falls back to
  // billed_at's own month for a fixture/old payload that predates the
  // field. Rendered as a long month + year in the active locale/calendar,
  // the same helper the billing-period pickers already use.
  const billingPeriodValue = invoice.billing_period ?? invoice.billed_at.slice(0, 7)
  const [billingPeriodYear, billingPeriodMonth] = billingPeriodValue.split('-').map(Number)
  const billingPeriodLabel = formatMonthYear(
    new Date(billingPeriodYear, billingPeriodMonth - 1, 1),
    { locale: i18n.language, calendar },
  )

  return (
    <>
      <div className="space-y-6">
        <ExploreCoachBanner pageKey="invoice" />
        <Breadcrumbs items={[{ label: t('title'), href: '/invoices' }, { label: invoice.number }]} />

        <PageHeader
          title={invoice.number}
          description={t('detail.billed_for_period', {
            date: formatDate(invoice.billed_at),
            period: billingPeriodLabel,
          })}
          isRefreshing={isFetching && !isLoading}
          // EP-106 Item 1 — mobile shows [ Record Payment ] [ ⋯ ]; the primary
          // is absent when paid, so Download promotes to solid.
          primaryAction={
            !invoice.paid
              ? {
                  key: 'record-payment',
                  label: t('payment.record'),
                  icon: CreditCard,
                  onClick: openPaymentModal,
                }
              : undefined
          }
          overflowActions={[
            {
              key: 'download',
              label: t('actions.download_pdf'),
              icon: Download,
              onClick: handleDownloadPdf,
              loading: isDownloading,
              disabled: !invoice?.signed_pdf_key && !invoiceTemplates?.data?.[0]?.template,
            },
            {
              key: 'sign',
              label: invoice.signed_at ? t('actions.re_sign') : t('actions.sign'),
              icon: PenLine,
              onClick: () => setShowSignModal(true),
              disabled: !invoiceTemplates?.data?.[0]?.template,
              hidden: !isAdminOrOwner,
            },
            {
              key: 'tax',
              label: t('tax_invoice.generate'),
              icon: FileBadge,
              onClick: handleOpenTaxInvoice,
              title: !accountSettings?.settings?.tax_id ? t('tax_invoice.no_tax_id_hint') : undefined,
              hidden: !(featureFlags.eTaxInvoice && isAdminOrOwner && invoice.paid && !invoice.tax_invoice_number),
            },
            {
              key: 'edit',
              label: tc('actions.edit'),
              icon: Edit,
              to: `/invoices/${id}/edit`,
              hidden: !canEdit,
            },
            {
              key: 'delete',
              label: tc('actions.delete'),
              icon: Trash2,
              onClick: () => setConfirmDelete(true),
              destructive: true,
            },
          ]}
        />

        <div className="grid gap-6 lg:grid-cols-3">
          {/* Invoice Summary */}
          {/* min-w-0: grid items default to min-width:auto, so without it this
              card inflates to the items table's intrinsic width and overflows
              the viewport on phones (the inner overflow-x-auto can only scroll
              once the grid child is allowed to shrink). */}
          <Card className="lg:col-span-2 min-w-0">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-semibold">{t('detail.title')}</h2>
              <InvoiceStatusBadge invoice={invoice} />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.billed_date')}
                </dt>
                <dd className="font-medium break-words">{formatDate(invoice.billed_at)}</dd>
              </div>
              <div className="min-w-0">
                <dt className="text-sm text-base-content/60 flex items-center gap-1">
                  <Calendar className="w-4 h-4" /> {t('labels.due_date')}
                </dt>
                <dd className="font-medium break-words">{formatDate(invoice.due_at)}</dd>
              </div>
            </div>

            {/* Invoice Items */}
            {invoice.items && invoice.items.length > 0 && (
              <div className="border-t border-base-200 pt-4">
                <h3 className="font-medium mb-3">{t('items.title')}</h3>
                <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('items.description')}</th>
                      <th className="text-right">{t('items.quantity')}</th>
                      <th className="text-right">{t('items.unit_price')}</th>
                      <th className="text-right">{t('items.amount')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice.items.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <div>{item.description}</div>
                          <Badge variant="ghost" className="mt-1">
                            {t(`item_types.${item.item_type}`)}
                          </Badge>
                        </td>
                        <td className="text-right">{item.quantity}</td>
                        <td className="text-right">{formatBaht(item.unit_price, { decimals: 2 })}</td>
                        <td className="text-right font-medium">{formatBaht(item.amount, { decimals: 2 })}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    {showVatBreakdown && (
                      <>
                        <tr className="border-t">
                          <td colSpan={3} className="text-right text-sm text-base-content/70">
                            {t('labels.subtotal')}
                          </td>
                          <td className="text-right text-sm">
                            {formatBaht(nonVatItemsSubtotal, { decimals: 2 })}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="text-right text-sm text-base-content/70">
                            {t('labels.vat', { rate: vatRate })}
                          </td>
                          <td className="text-right text-sm">
                            {formatBaht(engineVatAmount, { decimals: 2 })}
                          </td>
                        </tr>
                      </>
                    )}
                    <tr className="border-t-2">
                      <td colSpan={3} className="text-right font-semibold">
                        {tc('labels.total')}
                      </td>
                      <td className="text-right font-bold text-lg">
                        {formatBaht(invoice.total, { decimals: 2 })}
                      </td>
                    </tr>
                    {invoice.amount_paid > 0 && (
                      <>
                        <tr>
                          <td colSpan={3} className="text-right text-success">
                            {t('labels.paid_amount')}
                          </td>
                          <td className="text-right text-success">
                            -{formatBaht(invoice.amount_paid, { decimals: 2 })}
                          </td>
                        </tr>
                        <tr>
                          <td colSpan={3} className="text-right font-semibold">
                            {t('labels.balance_due')}
                          </td>
                          <td className="text-right font-bold text-lg">
                            {formatBaht(invoice.balance_due, { decimals: 2 })}
                          </td>
                        </tr>
                      </>
                    )}
                  </tfoot>
                </table>
                </div>
              </div>
            )}
            {invoice.paid && paymentDiff !== 0 && (
              <Alert
                variant={paymentDiff > 0 ? 'warning' : 'error'}
                icon={<AlertCircle className="w-4 h-4 shrink-0" />}
                role="alert"
                className="mt-4 py-2 px-3"
              >
                <span className="text-sm">
                  {t('labels.payment_difference', {
                    currency: '',
                    amount: formatBaht(Math.abs(paymentDiff), { decimals: 2 }),
                  })}
                </span>
              </Alert>
            )}
          </Card>

          {/* Sidebar */}
          <div className="space-y-6 min-w-0">
            {/* Customer Info */}
            {invoice.customer && (
              <Card>
                <h2 className="text-lg font-semibold mb-4">{t('labels.customer')}</h2>
                <Link
                  to={`/customers/${invoice.customer.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-base-200 transition-colors"
                >
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <User className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">{invoice.customer.name}</div>
                    <div className="text-sm text-base-content/60">{invoice.customer.phone}</div>
                  </div>
                </Link>
              </Card>
            )}

            {/* Unit Info */}
            {invoice.unit && (
              <Card>
                <h2 className="text-lg font-semibold mb-4">{t('labels.unit')}</h2>
                <div className="flex items-center gap-3 p-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Home className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-medium">{t('labels.unit')} {invoice.unit.name}</div>
                  </div>
                </div>
              </Card>
            )}

            {/* Signed Invoice Card */}
            {invoice.signed_at && (
              <Card className={`${isSignedStale ? 'bg-warning/5 border-warning' : 'bg-success/5 border-success'}`}>
                <div className="flex items-center gap-2 mb-3">
                  <PenLine className={`w-5 h-5 ${isSignedStale ? 'text-warning' : 'text-success'}`} />
                  <h2 className="font-semibold">{t('actions.sign')}</h2>
                  <Badge variant={isSignedStale ? 'warning' : 'success'} className="ml-auto">
                    {isSignedStale ? t('messages.signed_stale_badge') : tc('status.signed')}
                  </Badge>
                </div>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-base-content/60">{t('labels.signed_at')}</dt>
                    <dd className="font-medium">{formatDateTime(invoice.signed_at)}</dd>
                  </div>
                </dl>
                {isSignedStale && (
                  <Alert variant="warning" icon={<AlertCircle className="w-4 h-4 shrink-0" />} role="alert" className="mt-3 py-2 px-3">
                    <span className="text-sm">{t('messages.signed_stale')}</span>
                  </Alert>
                )}
                {invoice.signed_pdf_key && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                    leftIcon={<Download className="w-4 h-4" />}
                    onClick={() => window.open(`${import.meta.env.VITE_API_URL || ''}/api/images/${invoice.signed_pdf_key}`, '_blank', 'noopener,noreferrer')}
                  >
                    {t('actions.download_signed_pdf')}
                  </Button>
                )}
                {isAdminOrOwner && isSignedStale && (
                  <Button
                    variant="outline"
                    size="sm"
                    className="mt-3 w-full"
                    leftIcon={<PenLine className="w-4 h-4" />}
                    onClick={() => setShowSignModal(true)}
                  >
                    {t('actions.re_sign')}
                  </Button>
                )}
              </Card>
            )}

            {/* Tax Invoice Card (EP-37) */}
            {featureFlags.eTaxInvoice && invoice.tax_invoice_number ? (
              <Card className="bg-info/5 border-info">
                <div className="flex items-center gap-2 mb-3">
                  <FileBadge className="w-5 h-5 text-info" />
                  <h2 className="font-semibold">{t('tax_invoice.title')}</h2>
                </div>
                <dl className="space-y-2">
                  <div>
                    <dt className="text-sm text-base-content/60">{t('tax_invoice.number')}</dt>
                    <dd className="font-medium">{invoice.tax_invoice_number}</dd>
                  </div>
                  {invoice.tax_invoice_date && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('tax_invoice.date')}</dt>
                      <dd className="font-medium">{formatDate(invoice.tax_invoice_date)}</dd>
                    </div>
                  )}
                  {invoice.tax_seller_name && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('tax_invoice.seller_name')}</dt>
                      <dd className="font-medium text-sm">{invoice.tax_seller_name}</dd>
                    </div>
                  )}
                  {invoice.tax_buyer_name && (
                    <div>
                      <dt className="text-sm text-base-content/60">{t('tax_invoice.buyer_name')}</dt>
                      <dd className="font-medium text-sm">{invoice.tax_buyer_name}</dd>
                    </div>
                  )}
                </dl>
                {isTaxInvoiceStale && (
                  <Alert variant="warning" icon={<AlertCircle className="w-4 h-4 shrink-0" />} role="alert" className="mt-4 py-2 px-3">
                    <span className="text-sm">{t('tax_invoice.vat_changed')}</span>
                  </Alert>
                )}
                {invoice.tax_invoice_pdf_url && (
                  <div className="flex gap-2 mt-4">
                    <Button
                      variant="outline"
                      size="sm"
                      leftIcon={<Download className="w-4 h-4" />}
                      onClick={handleDownloadTaxInvoicePdf}
                    >
                      {t('tax_invoice.download_pdf')}
                    </Button>
                    {isTaxInvoiceStale && (
                      <Button
                        variant="outline"
                        size="sm"
                        leftIcon={<FileBadge className="w-4 h-4" />}
                        onClick={handleRegeneratePdf}
                        loading={isRegenerating}
                      >
                        {t('tax_invoice.regenerate_pdf')}
                      </Button>
                    )}
                  </div>
                )}
              </Card>
            ) : featureFlags.eTaxInvoice && isAdminOrOwner && invoice.paid ? (
              <Card>
                <div className="flex items-center gap-2 mb-2">
                  <FileBadge className="w-5 h-5 text-base-content/40" />
                  <h2 className="font-semibold">{t('tax_invoice.title')}</h2>
                </div>
                <p className="text-sm text-base-content/60 mb-3">
                  {accountSettings?.settings?.tax_id ? t('tax_invoice.generate_confirm') : t('tax_invoice.no_tax_id_hint')}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={handleOpenTaxInvoice}
                >
                  {t('tax_invoice.generate')}
                </Button>
              </Card>
            ) : null}

            {/* Summary Card */}
            <Card
              className={`${
                invoice.status === 'paid' ? 'bg-success/5 border-success' : invoice.status === 'overdue' ? 'bg-error/5 border-error' : ''
              }`}
            >
              <div className="flex items-center gap-3 mb-4">
                {invoice.status === 'paid' ? (
                  <CheckCircle className="w-8 h-8 text-success" />
                ) : invoice.status === 'overdue' ? (
                  <AlertCircle className="w-8 h-8 text-error" />
                ) : (
                  invoice.status === 'partially_paid' ? <CreditCard className="w-8 h-8 text-info" /> : <Receipt className="w-8 h-8 text-warning" />
                )}
                <div>
                  <div className="text-sm text-base-content/60">
                    {invoice.status === 'paid' ? t('status.fully_paid') : invoice.status === 'overdue' ? t('status.payment_overdue') : invoice.status === 'partially_paid' ? t('status.partially_paid') : t('status.payment_due')}
                  </div>
                  <div className="text-sm text-base-content/60">{t('labels.balance_due')}</div>
                  <div className="text-2xl font-bold">
                    {formatBaht(invoice.balance_due, { decimals: 2 })}
                  </div>
                </div>
              </div>
              {!invoice.paid && (
                <Button className="w-full" onClick={openPaymentModal}>
                  {t('payment.record')}
                </Button>
              )}
            </Card>
          </div>
        </div>

        {/* Payments */}
        {invoice.payments && invoice.payments.length > 0 && (
          <Card>
            <h2 className="text-lg font-semibold mb-4">{t('payment.title')}</h2>
            <div className="overflow-x-auto">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t('payment.date')}</th>
                    <th>{t('payment.method')}</th>
                    <th>{t('payment.reference')}</th>
                    <th>{t('payment.slip')}</th>
                    <th className="text-right">{tc('labels.amount')}</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {invoice.payments.map((payment) => (
                    <tr key={payment.id} className="hover">
                      <td>{formatDate(payment.paid_at)}</td>
                      <td>
                        <Badge variant="ghost">
                          {t(`payment.methods.${payment.payment_method}`)}
                        </Badge>
                      </td>
                      <td className="text-base-content/60">{payment.reference || '-'}</td>
                      <td>
                        {payment.slip_url ? (
                          <a
                            href={`${import.meta.env.VITE_API_URL || ''}/api/images/${payment.slip_url}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            <Image className="w-4 h-4" />
                            <span className="text-sm">{tc('actions.view')}</span>
                          </a>
                        ) : payment.payment_method === 'transfer' ? (
                          <Badge variant="error" size="sm">{t('payment.missing_slip')}</Badge>
                        ) : (
                          <span className="text-base-content/40">-</span>
                        )}
                      </td>
                      <td className="text-right font-medium text-success">
                        {formatBaht(payment.amount, { decimals: 2 })}
                      </td>
                      <td className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            size="xs"
                            variant="ghost"
                            data-testid="payment-row-view"
                            onClick={() => navigate(`/payments/${payment.id}`)}
                            title={tc('actions.view')}
                            aria-label={tc('actions.view')}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => handleDownloadReceipt(payment.id)}
                            loading={downloadingReceiptId === payment.id}
                            title={t('payment.download_receipt')}
                            aria-label={t('payment.download_receipt')}
                          >
                            <FileText className="w-4 h-4" />
                          </Button>
                          <Button
                            size="xs"
                            variant="ghost"
                            className="text-error"
                            onClick={() => setConfirmDeletePaymentId(payment.id)}
                            aria-label={tc('actions.delete')}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {/* Sign Invoice Modal */}
      <SignInvoiceModal
        isOpen={showSignModal}
        onClose={() => setShowSignModal(false)}
        onSign={handleSignInvoice}
        loading={signInvoice.isPending}
        title={invoice?.signed_at ? t('actions.re_sign') : t('actions.sign')}
        invoice={invoice}
        template={invoiceTemplates?.data?.[0]?.template as Template | undefined}
        accountSettings={accountSettings}
        signerName={user?.full_name || user?.email || ''}
      />

      {/* Generate Tax Invoice Modal */}
      <Modal
        isOpen={showTaxInvoiceModal}
        onClose={() => setShowTaxInvoiceModal(false)}
        title={t('tax_invoice.generate')}
        size="lg"
        footer={
          <>
            <Button variant="ghost" onClick={() => setShowTaxInvoiceModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              onClick={handleSubmitTax(onSubmitTaxInvoice)}
              loading={generateTaxInvoice.isPending}
            >
              {t('tax_invoice.generate')}
            </Button>
          </>
        }
      >
        <form className="space-y-5">
          <div>
            <h4 className="font-semibold text-sm text-base-content/70 uppercase tracking-wide mb-3">
              {t('tax_invoice.seller_section')}
            </h4>
            <div className="space-y-3">
              <Input
                label={t('tax_invoice.seller_name')}
                error={taxErrors.seller_name?.message}
                {...registerTax('seller_name', { required: tc('errors.required') })}
              />
              <Input
                label={t('tax_invoice.seller_tax_id')}
                error={taxErrors.seller_tax_id?.message}
                {...registerTax('seller_tax_id')}
              />
              <Input
                label={t('tax_invoice.seller_branch')}
                error={taxErrors.seller_branch?.message}
                {...registerTax('seller_branch')}
              />
              <Textarea
                label={t('tax_invoice.seller_address')}
                rows={3}
                error={taxErrors.seller_address?.message}
                {...registerTax('seller_address')}
              />
            </div>
          </div>
          <div className="divider my-0" />
          <div>
            <h4 className="font-semibold text-sm text-base-content/70 uppercase tracking-wide mb-3">
              {t('tax_invoice.buyer_section')}
            </h4>
            <div className="space-y-3">
              <Input
                label={t('tax_invoice.buyer_name')}
                error={taxErrors.buyer_name?.message}
                {...registerTax('buyer_name', { required: tc('errors.required') })}
              />
              <Input
                label={t('tax_invoice.buyer_tax_id')}
                error={taxErrors.buyer_tax_id?.message}
                {...registerTax('buyer_tax_id')}
              />
              <Textarea
                label={t('tax_invoice.buyer_address')}
                rows={3}
                error={taxErrors.buyer_address?.message}
                {...registerTax('buyer_address')}
              />
            </div>
          </div>
        </form>
      </Modal>

      {/* Delete Invoice Confirm */}
      <ConfirmDialog
        isOpen={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        onConfirm={handleDelete}
        title={tc('actions.delete')}
        message={t('messages.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deleteInvoice.isPending}
      />

      {/* Delete Payment Confirm */}
      <ConfirmDialog
        isOpen={confirmDeletePaymentId !== null}
        onClose={() => setConfirmDeletePaymentId(null)}
        onConfirm={handleDeletePayment}
        title={tc('actions.delete')}
        message={t('payment.delete_confirm')}
        confirmLabel={tc('actions.delete')}
        cancelLabel={tc('actions.cancel')}
        variant="error"
        loading={deletePayment.isPending}
      />

      {/* Payment Modal — body delegated to PaymentFields (task-019) */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title={t('payment.record')}
      >
        <PaymentFields
          payment={null}
          invoiceId={invoiceId}
          onSave={() => {
            setShowPaymentModal(false)
          }}
        />
      </Modal>
    </>
  )
}
